import React from 'react';

import { useIntl } from 'react-intl';

import { useFlags } from '@atlaskit/flag';
import SuccessIcon from '@atlaskit/icon/core/check-mark';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { auth } from '@atlaskit/outbound-auth-flow-client';
import { token } from '@atlaskit/tokens';
import {
	generate3pHash,
	SEARCH_3P_CONFIG,
	searchPageQueryCache,
	sendSlackAuthSuccessRequest,
} from '@atlassian/dynamic-cards-search-client';

import { type ProductKeys } from '../../../../../../common/constants/products';
import type { OutboundAuthConfigsType } from '../../../../../../common/types';
import { useBootstrap, useSearchActions } from '../../../../../../controllers/store';

import { messages } from './messages';
import { OAuthMessage } from './oauth-message';
import { increaseProductDismissCount } from './utils';

type OAuthBannerProps = {
	outboundAuthUrl: string;
	product: ProductKeys;
};

export const OAuthBanner = ({
	product,
	outboundAuthUrl,
}: OAuthBannerProps): React.JSX.Element | null => {
	const [{ cloudId, user, outboundAuthConfigs }, { setState }] = useBootstrap();
	const userId = user?.id;
	const { retrySearch } = useSearchActions();
	const { formatMessage } = useIntl();
	const [isDismissed, setIsDismissed] = React.useState(false);
	const { showFlag } = useFlags();
	const productCapitalized = product.charAt(0).toUpperCase() + product.slice(1);

	const onSuccess = () => {
		if (product === 'slack') {
			sendSlackAuthSuccessRequest({ cloudId });
		}
		setIsDismissed(true);
		showFlag({
			title: formatMessage(messages.authorizationSuccess, {
				product: productCapitalized,
			}),
			icon: <SuccessIcon spacing="spacious" label="" color={token('color.icon.success')} />,
			isAutoDismiss: true,
		});

		const newOutboundAuthConfigs = refreshOAuthCache(
			product,
			userId || '',
			cloudId || '',
			outboundAuthConfigs,
		);

		setState({ outboundAuthConfigs: newOutboundAuthConfigs });
		searchPageQueryCache.clear();
		retrySearch();
	};
	const onFail = () => {
		showFlag({
			title: formatMessage(messages.authorizationFail, {
				product: productCapitalized,
			}),
			icon: <StatusErrorIcon spacing="spacious" label="" color={token('color.icon.danger')} />,
			isAutoDismiss: true,
		});
	};

	const onAuthorize = () => {
		auth(outboundAuthUrl).then(onSuccess).catch(onFail);
	};

	const onDismiss = () => {
		setIsDismissed(true);
		increaseProductDismissCount(product);
	};

	if (isDismissed) {
		return null;
	}

	return (
		<OAuthMessage product={product} onAuthorize={onAuthorize} onDismiss={onDismiss}></OAuthMessage>
	);
};

const refreshOAuthCache = (
	product: ProductKeys,
	userId: string,
	cloudId: string,
	outboundAuthConfigs: OutboundAuthConfigsType,
) => {
	const key = generate3pHash(userId, cloudId);

	const item = localStorage.getItem(SEARCH_3P_CONFIG);

	if (!item) {
		return;
	}
	let object;
	try {
		object = JSON.parse(item);
	} catch (_) {
		return;
	}

	const oauthConfig = object?.[key]?.result?.find((config: any) => config.type.includes(product));
	if (!oauthConfig) {
		return;
	}
	oauthConfig.isUserOAuthed = true;
	const newOutboundAuthConfigs = { ...outboundAuthConfigs };
	const updatedConfig = outboundAuthConfigs[product];
	if (updatedConfig) {
		updatedConfig.userNeedsOAuth = false;
	}

	localStorage.setItem(SEARCH_3P_CONFIG, JSON.stringify(object));

	return newOutboundAuthConfigs;
};
